import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  Router,
  provideRouter,
  withPreloading,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import Jsona from 'jsona';
import * as sentryConfig from '../sentry.config.json';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { JSONAPIInterceptor } from './app/interceptors/jsonapi.interceptor';
import { requestIdInterceptor } from './app/interceptors/request-id.interceptor';
import { provideGeolocation } from './app/providers/geolocation/geolocation.service';
import { CurrencyService } from './app/services/currency.service';
import { LanguageService } from './app/services/language.service';
import { ConsoleLoggerBackend, LOGGER_BACKEND, SentryLoggerBackend } from './app/services/logger';
import { TranslationsRefreshService } from './app/services/i18n/translations-refresh.service';
import {
  appTranslateInitializer,
  provideTranslate,
} from './app/translate.providers';
import { environment } from './environments/environment';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { providePushNotifications } from './app/services/push-notifications/push-notifications.provider';
import { provideCapacitorApp } from './app/providers/capacitor-app/capacitor-app.provider';
import { provideNativeSettings } from './app/providers/native-settings/native-settings.provider';
import { provideCapacitorBrowser } from './app/providers/capacitor-browser/capacitor-browser.provider';
import { provideSentrySetUser } from './app/providers/sentry/sentry.provider';

defineCustomElements(window);

if (environment.sentryDSN) {
  Sentry.init({
    release: sentryConfig.commit,
    dist: sentryConfig.dist,
    dsn: environment.sentryDSN,
    environment: environment.sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    enableLogs: true,
    sendDefaultPii: true,
  });
}

function appInitializer() {
  return async () => {
    if (environment.sentryDSN) {
      inject(Sentry.TraceService);
    }

    const runTranslateInit = appTranslateInitializer();
    const translationsRefresh = inject(TranslationsRefreshService);

    await runTranslateInit();

    const bootLang = 'en';

    await translationsRefresh.applyCachedIfPresent(bootLang);

    void translationsRefresh.refreshFromRemote(bootLang);
  };
}

registerLocaleData(localeDe, 'de-DE');

bootstrapApplication(AppComponent, {
  providers: [
    ...(environment.sentryDSN
      ? [
          { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
          { provide: Sentry.TraceService, deps: [Router] },
        ]
      : []),
    {
      provide: LOGGER_BACKEND,
      useValue: environment.sentryDSN ? new SentryLoggerBackend(Sentry.logger) : new ConsoleLoggerBackend(),
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: Jsona, useValue: new Jsona() },
    provideIonicAngular({
      mode: 'md',
      innerHTMLTemplatesEnabled: true,
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptors([requestIdInterceptor, JSONAPIInterceptor])),
    provideTranslate(),
    provideAppInitializer(appInitializer()),
    LanguageService,
    CurrencyService,
    provideGeolocation(),
    providePushNotifications(),
    provideNativeSettings(),
    provideCapacitorApp(),
    provideCapacitorBrowser(),
    provideSentrySetUser(),
  ],
});

// Initialize Google Tag Manager (Web + Capacitor WebView on iOS/Android)
if (environment.google?.tagManager?.id) {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${environment.google.tagManager.id}`;
  document.head.appendChild(gtmScript);
}

// Initialize CookieFirst consent banner
if (
  Capacitor.getPlatform() === 'web' &&
  !window.location.hostname.includes('localhost')
) {
  const script = document.createElement('script');
  script.src =
    'https://consent.cookiefirst.com/sites/movacar.com-1a7f3ed5-d1bd-4c07-979a-e74a79be3753/consent.js';

  document.head.appendChild(script);

  // Adjust CookieFirst floating button position for mobile browsers
  let buttonFound = false;

  const adjustCookieButtonPosition = () => {
    const cookieButton = document.querySelector(
      'span[data-cookiefirst-action="open-preferences"].cfGMLl'
    ) as HTMLElement;

    if (cookieButton) {
      if (window.innerWidth < 768) {
        cookieButton.style.bottom = '85px';
      } else {
        cookieButton.style.bottom = '15px'; // default position
      }

      buttonFound = true;

      return true;
    }

    return false;
  };

  const observer = new MutationObserver(() => {
    if (adjustCookieButtonPosition()) {
      observer.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('resize', adjustCookieButtonPosition);

  setTimeout(() => {
    if (!buttonFound) {
      observer.disconnect();
    }
  }, 10000);
}
